import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  useDashboardBreakpoint,
  trackPathfinderDashboardView,
  Modal,
  useT,
} from '@repo/common';
import Sidebar from '../../components/PathfinderDashboard/Overview/Sidebar/Sidebar';
import UserInfo from '../../components/PathfinderDashboard/Overview/UserInfo/UserInfo';
import ReadinessScore from '../../components/PathfinderDashboard/Overview/UserInfo/ReadinessScore';
import Workshops from '../../components/PathfinderDashboard/Overview/UserInfo/Workshops';
import Dashboard from '../../components/PathfinderDashboard/Overview/Dashboard/Dashboard';
import ActiveDefaultPage from '../../components/PathfinderDashboard/Overview/Dashboard/Active/ActiveDefaultPage';
import DashboardLayout from '../../components/Layout/DashboardLayout';
import ComingSoonPage from '../../components/Common/ComingSoonPage';
import {
  evaluatePathfinderSession,
  getPathfinderEvidenceCards,
  getLatestPathfinderSession,
} from '../../api/pathfinder/pathfinder.api';
import { getUpcomingConfirmedCount } from '../../api/booking/booking.api';

const PATHFINDER_DRAFT_STORAGE_KEY = 'pathfinderDraft';

const readPathfinderSessionId = () => {
  try {
    const draft = JSON.parse(
      localStorage.getItem(PATHFINDER_DRAFT_STORAGE_KEY) || 'null',
    );
    return draft?.sessionId || null;
  } catch {
    return null;
  }
};

const hasSavedScore = (session) =>
  session?.finalScore !== null && session?.finalScore !== undefined;
const hasCompletedOnboarding = (session) =>
  session?.status === 'COMPLETED' || hasSavedScore(session);
const DASHBOARD_SECTION_INDEX = {
  dashboard: 0,
  diagnostic: 1,
  active: 2,
};

const getDashboardSectionIndex = (section) =>
  DASHBOARD_SECTION_INDEX[String(section || '').toLowerCase()] ?? 0;

const getDashboardSectionParam = (searchParams) =>
  searchParams.get('section') || searchParams.get('tab');

const PathfinderDashboardLoadingScreen = () => {
  const t = useT();
  return (
    <div className='flex items-center justify-center w-full h-screen bg-grayscale-gray01'>
      <p className='typo-b16Strong text-text-secondary'>
        {t('Loading...', '불러오는 중...')}
      </p>
    </div>
  );
};

const PathfinderDashboard = () => {
  const t = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeIndex, setActiveIndex] = useState(() =>
    getDashboardSectionIndex(getDashboardSectionParam(searchParams)),
  );
  const [sessionId, setSessionId] = useState(null);
  const [evaluationData, setEvaluationData] = useState(null);
  const [evidenceCards, setEvidenceCards] = useState(null);
  const [isPathfinderLoading, setIsPathfinderLoading] = useState(true);
  const [requiresOnboarding, setRequiresOnboarding] = useState(false);
  const [pathfinderError, setPathfinderError] = useState(null);
  const bp = useDashboardBreakpoint();
  const isDesktop = ['desktopS', 'desktopL'].includes(bp);
  const isTablet = bp === 'tabletV';
  const isMobile = ['mobileMini', 'mobile', 'tabletH'].includes(bp);

  useEffect(() => {
    let alive = true;
    const fetchPathfinderData = async () => {
      setIsPathfinderLoading(true);
      setPathfinderError(null);

      try {
        const latestSession = await getLatestPathfinderSession();

        if (!hasCompletedOnboarding(latestSession)) {
          if (!alive) return;
          setRequiresOnboarding(true);
          setSessionId(null);
          setEvaluationData(null);
          setEvidenceCards(null);
          return;
        }

        const nextSessionId = latestSession.id || readPathfinderSessionId();

        const [evaluation, cards] = await Promise.all([
          hasSavedScore(latestSession)
            ? Promise.resolve(latestSession)
            : evaluatePathfinderSession(nextSessionId, true),
          getPathfinderEvidenceCards(nextSessionId),
          getUpcomingConfirmedCount().catch(() => 0),
        ]);

        if (!alive) return;
        setRequiresOnboarding(false);
        setSessionId(nextSessionId);
        setEvaluationData(evaluation);
        setEvidenceCards(cards);

        trackPathfinderDashboardView({
          path_type: 'job_seeker', //TODO: GA4 > 하드코딩 변경하기
          current_step: evaluation?.currentStep || 'step_01',
          workshops_count: upcomingCount ?? 0,
          courses_count: 0,
          source: searchParams.get('source') || 'gnb_pathfinder',
        });
      } catch (error) {
        if (!alive) return;
        console.error(error);
        setPathfinderError(error);
      } finally {
        if (alive) setIsPathfinderLoading(false);
      }
    };

    fetchPathfinderData();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    setActiveIndex(
      getDashboardSectionIndex(getDashboardSectionParam(searchParams)),
    );
  }, [searchParams]);

  const handleLater = () => {
    navigate('/', { replace: true });
  };

  const handleStartPathfinder = () => {
    navigate('/pathfinder', { replace: true });
  };

  const refreshEvidenceCards = async () => {
    if (!sessionId) return;
    const cards = await getPathfinderEvidenceCards(sessionId);
    setEvidenceCards(cards);
  };

  const handleEvidenceUpdated = async (nextEvaluation) => {
    if (nextEvaluation) {
      setEvaluationData((prev) => ({
        ...prev,
        ...nextEvaluation,
        recommendationData:
          nextEvaluation.recommendationData || prev?.recommendationData,
      }));
    }
    await refreshEvidenceCards();
  };

  const pages = [
    {
      sidebar: (
        <Sidebar activeIndex={activeIndex} setActiveIndex={setActiveIndex} />
      ),
      leftPanel: isDesktop ? (
        <div className='flex flex-col w-full h-full gap-6'>
          <div className='flex-shrink-0'>
            <UserInfo />
          </div>
          <div className='flex-shrink-0'>
            <ReadinessScore
              score={evaluationData?.finalScore}
              stage={evaluationData?.finalStage}
            />
          </div>
          <div className='flex-1 min-h-0 max-h-[480px]'>
            <Workshops />
          </div>
        </div>
      ) : isTablet ? (
        <div className='flex items-center gap-4 w-full max-h-[200px]'>
          <div className='flex items-center flex-shrink-0 gap-2'>
            <UserInfo />
            <ReadinessScore
              score={evaluationData?.finalScore}
              stage={evaluationData?.finalStage}
            />
          </div>
          <div className='flex-1 h-full min-w-0'>
            <Workshops />
          </div>
        </div>
      ) : (
        <UserInfo />
      ),
      main: <Dashboard evaluationData={evaluationData} />,
    },
    {
      sidebar: (
        <Sidebar activeIndex={activeIndex} setActiveIndex={setActiveIndex} />
      ),
      leftPanel: null,
      main: (
        <ComingSoonPage
          embedded
          title='Analytics Dashboard'
          titleKr='분석 대시보드'
          description='Detailed analytics for your Korea readiness are on the way. Check back soon.'
          descriptionKr='한국 정착 준비도를 자세히 분석해 주는 기능을 준비하고 있어요. 조금만 기다려 주세요.'
        />
      ),
    },
    {
      sidebar: (
        <Sidebar activeIndex={activeIndex} setActiveIndex={setActiveIndex} />
      ),
      leftPanel: null,
      main: (
        <ActiveDefaultPage
          sessionId={sessionId}
          evaluationData={evaluationData}
          evidenceCards={evidenceCards}
          isLoading={isPathfinderLoading}
          error={pathfinderError}
          onEvidenceUpdated={handleEvidenceUpdated}
        />
      ),
    },
  ];

  const current = pages[activeIndex];

  if (requiresOnboarding) {
    return (
      <>
        <PathfinderDashboardLoadingScreen />
        <Modal
          isOpen
          type='default'
          title={t('Start Pathfinder first', 'Pathfinder를 먼저 시작해 주세요')}
          subText={t(
            `Complete the Pathfinder onboarding\nto access your dashboard and personalized journey in Korea.`,
            `Pathfinder 온보딩을 완료하면\n대시보드와 나만의 한국 여정을 확인할 수 있어요.`,
          )}
          leftButtonLabel={t('Later', '나중에')}
          rightButtonLabel={t('Start for free', '무료로 시작하기')}
          leftHideIcon
          rightHideIcon
          buttonSize='xl'
          onClickLeftBtn={handleLater}
          onClickRightBtn={handleStartPathfinder}
        />
      </>
    );
  }

  if (isPathfinderLoading && !evaluationData) {
    return <PathfinderDashboardLoadingScreen />;
  }

  return (
    <DashboardLayout
      sidebar={current.sidebar}
      leftPanel={current.leftPanel}
      fitViewport={activeIndex === 0}
    >
      <div className={activeIndex === 0 ? 'h-full min-h-0' : ''}>
        {current.main}
      </div>

      {isMobile && activeIndex === 0 && (
        <div className='flex-shrink-0 w-full mt-6' style={{ height: '580px' }}>
          <Workshops />
        </div>
      )}
    </DashboardLayout>
  );
};

export default PathfinderDashboard;
